import React from 'react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from './PermissionContext';
import { getRequiredPermissions, isAlwaysAllowed } from '../config/routePermissions';
import { getRouteModule, isRouteEnabled } from '../config/modules';

// =============================================
// 0. AccessDenied — shared denial screen
// =============================================
export const AccessDenied = ({ detail }) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4 p-8 text-center">
    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
      <span className="text-3xl">🔒</span>
    </div>
    <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
    <p className="text-gray-500 text-sm max-w-md">
      You don't have permission to view this page. Please contact your
      administrator if you need access.
    </p>
    {/* Technical detail sirf dev me — end user ko code names nahi dikhane */}
    {detail && process.env.NODE_ENV !== 'production' && (
      <p className="text-[11px] font-mono text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-1">
        {detail}
      </p>
    )}
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <div className="w-8 h-8 border-4 border-[#459B7A] border-t-transparent rounded-full animate-spin" />
  </div>
);

// =============================================
// 0.5 ModuleNotEnabled / ModuleGuard — client deployment me module off hai
//     (config/modules.js). Ye ADMIN ke liye bhi block hai.
// =============================================
export const ModuleNotEnabled = ({ module }) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4 p-8 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
      <span className="text-3xl">🧩</span>
    </div>
    <h2 className="text-xl font-semibold text-gray-700">Module Not Enabled</h2>
    <p className="text-gray-500 text-sm max-w-md">
      This feature is not part of your CRM plan. Please contact your
      administrator if you need it.
    </p>
    {module && process.env.NODE_ENV !== 'production' && (
      <p className="text-[11px] font-mono text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-1">
        Module "{module}" is off — add it to REACT_APP_MODULES
      </p>
    )}
  </div>
);

export const ModuleGuard = ({ children }) => {
  const location = useLocation();
  if (!isRouteEnabled(location.pathname)) {
    return <ModuleNotEnabled module={getRouteModule(location.pathname)} />;
  }
  return <>{children}</>;
};

// =============================================
// 1. RoutePermissionGuard — har route par lagta hai (FAIL-CLOSED)
//    routePermissions.js ke map se current path ki permission check karta hai.
//    Map me route nahi mila = block.
// =============================================
export const RoutePermissionGuard = ({ children }) => {
  const location = useLocation();
  const { hasAny, isAdmin, loading, permissions } = usePermissions();

  // Module off hai to kisi ko nahi (ADMIN ko bhi)
  if (!isRouteEnabled(location.pathname)) {
    return <ModuleNotEnabled module={getRouteModule(location.pathname)} />;
  }

  // ADMIN sab kuch dekh sakta hai
  if (isAdmin) return <>{children}</>;

  // Har logged-in user ke liye khule routes (dashboard, my-profile)
  if (isAlwaysAllowed(location.pathname)) return <>{children}</>;

  // Pehli baar permissions aa rahi hain (koi cache nahi) — spinner
  if (loading && permissions.length === 0) return <Spinner />;

  const required = getRequiredPermissions(location.pathname);

  // FAIL-CLOSED — route map me nahi hai to block
  if (!required) {
    return (
      <AccessDenied
        detail={`No permission mapped for ${location.pathname} — add it to routePermissions.js`}
      />
    );
  }

  if (!hasAny(required)) {
    return <AccessDenied detail={`Required: ${required.join(' or ')}`} />;
  }

  return <>{children}</>;
};

// =============================================
// 2. PermissionGate - koi bhi cheez wrap karo
//    permission hogi to dikhegi, nahi to fallback
// =============================================
export const PermissionGate = ({ permission, permissions, requireAll = false, fallback = null, children }) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let allowed = false;

  if (permission) {
    allowed = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    allowed = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  } else {
    // No permission required - always show
    allowed = true;
  }

  if (!allowed) return fallback;
  return <>{children}</>;
};

// =============================================
// 3. ProtectedPage - pura page protect karo
//    Permission nahi = "Access Denied" page dikhao
// =============================================
export const ProtectedPage = ({ permission, permissions, requireAll = false, children }) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading, permissions: myPerms } = usePermissions();

  if (loading && myPerms.length === 0) return <Spinner />;

  let allowed = false;

  if (permission) {
    allowed = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    allowed = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  } else {
    allowed = true;
  }

  if (!allowed) {
    const req = permission || (permissions || []).join(requireAll ? ' and ' : ' or ');
    return <AccessDenied detail={req ? `Required: ${req}` : null} />;
  }

  return <>{children}</>;
};

// =============================================
// 4. PermissionButton - permission nahi = button hidden
// =============================================
export const PermissionButton = ({ permission, children, ...props }) => {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) return null;

  return <button {...props}>{children}</button>;
};

// =============================================
// 5. PermissionFilter - filter wrapper
//    permission nahi = filter hidden rahega
// =============================================
export const PermissionFilter = ({ permission, children }) => {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) return null;

  return <>{children}</>;
};
